import React, { useEffect, useMemo, useRef, useState } from "react";
import SandTris from "./widgets/sandtrix";
/**
 * Widgets system
 * - docked=true  => render panels in-flow (stacked, scrollable page)
 * - docked=false => render draggable fixed windows (desktop)
 */

export default function Widgets({ open, onClose, anchorEl, docked }) {
  const [sandtrisOpen, setSandtrisOpen] = useState(false);

  // If menu closes, keep widgets open (your requested behavior)
  // (so no auto-reset here)

  if (!open && !sandtrisOpen) return null;

  return (
    <>
      <style>{`
        .w-panel{
          width: 100%;
          border: 2px solid var(--term-frame);
          background: rgba(0,0,0,0.55);
          box-shadow: 0 0 16px rgba(0,0,0,0.45);
          backdrop-filter: blur(2px);
        }

        .w-win{
          position: fixed;
          z-index: 50;
          width: 520px;
          max-width: calc(100vw - 24px);
          border: 2px solid var(--term-frame);
          background: rgba(0,0,0,0.55);
          box-shadow: 0 0 16px rgba(0,0,0,0.45);
          backdrop-filter: blur(2px);
        }

        .w-titlebar{
          user-select:none;
          -webkit-user-select:none;
          touch-action:none;
          cursor: grab;
          padding: 10px 12px;
          border-bottom: 1px solid var(--term-frame-soft);
          display:flex;
          align-items:center;
          gap:10px;
          letter-spacing: .08em;
          font-size: 12px;
        }
        .w-title{ color: var(--term-fg); opacity: .95; font-weight: 700; }
        .w-spacer{ margin-left:auto; }
        .w-close{
          border: 1px solid var(--term-frame-soft);
          background: rgba(0,0,0,0.35);
          color: var(--term-fg);
          padding: 4px 10px;
          letter-spacing: .08em;
          cursor: pointer;
        }
        .w-body{
          padding: 10px 12px 12px;
          color: var(--term-fg);
          font-size: 12px;
        }

        .w-row{
          border: 1px solid var(--term-frame-soft);
          padding: 10px 10px;
          margin-top: 10px;
          cursor: pointer;
          background: rgba(0,0,0,0.18);
        }
        .w-row:hover{ background: rgba(255,255,255,0.04); }
        .w-hint{ margin-top: 10px; opacity: .85; font-size: 11px; }

        /* Dock mode: no drag cursor */
        .dock .w-titlebar{ cursor: default; touch-action: auto; }
      `}</style>

      {/* DOCKED (mobile): stack under terminal */}
      {docked && (
        <>
          {open && (
            <Panel title="WIDGETS" onClose={onClose}>
              <div style={{ fontWeight: 700, opacity: 0.95 }}>AVAILABLE:</div>

              <div className="w-row" onClick={() => setSandtrisOpen(true)}>
                SANDTRIS
              </div>

              <div className="w-hint">
                Tip: menu stays open. Each widget is a separate window/panel.
              </div>
            </Panel>
          )}

          {sandtrisOpen && (
            <Panel
              title="SANDTRIS v0.1"
              onClose={() => setSandtrisOpen(false)}
              bodyStyle={{ padding: 10 }}
            >
              <SandTris />
            </Panel>
          )}
        </>
      )}

      {/* FLOATING (desktop): draggable windows */}
      {!docked && (
        <>
          {open && (
            <Window
              title="WIDGETS"
              anchorEl={anchorEl}
              defaultOffset={{ x: -320, y: -220 }}
              onClose={onClose}
            >
              <div style={{ fontWeight: 700, opacity: 0.95 }}>AVAILABLE:</div>

              <div className="w-row" onClick={() => setSandtrisOpen(true)}>
                SANDTRIS
              </div>

              <div className="w-hint">
                Tip: menu stays open. Each widget is a separate draggable window.
              </div>
            </Window>
          )}

          {sandtrisOpen && (
            <Window
              title="SANDTRIS v0.1"
              anchorEl={anchorEl}
              defaultOffset={{ x: -320, y: 60 }}
              onClose={() => setSandtrisOpen(false)}
              bodyStyle={{ padding: 10 }}
            >
              <SandTris />
            </Window>
          )}
        </>
      )}
    </>
  );
}

/* ================= DOCK PANEL (in-flow) ================= */

function Panel({ title, onClose, children, bodyStyle }) {
  return (
    <div className="w-panel">
      <div className="w-titlebar">
        <div className="w-title">{title}</div>
        <div className="w-spacer" />
        <button className="w-close" onClick={onClose}>
          CLOSE
        </button>
      </div>
      <div className="w-body" style={bodyStyle}>
        {children}
      </div>
    </div>
  );
}

/* ================= DRAGGABLE WINDOW (fixed) ================= */

function Window({ title, onClose, children, anchorEl, defaultOffset, bodyStyle }) {
  const rect = anchorEl?.getBoundingClientRect?.();
  const baseLeft = rect ? rect.left : window.innerWidth - 360;
  const baseTop = rect ? rect.top : 120;

  const [pos, setPos] = useState(() => ({
    left: Math.max(12, Math.round(baseLeft + (defaultOffset?.x ?? 0))),
    top: Math.max(12, Math.round(baseTop + (defaultOffset?.y ?? 0))),
  }));

  const clamp = (left, top) => {
    const margin = 12;
    const w = 520;
    const h = 260;
    const maxLeft = Math.max(margin, window.innerWidth - w - margin);
    const maxTop = Math.max(margin, window.innerHeight - h - margin);
    return {
      left: Math.min(Math.max(margin, left), maxLeft),
      top: Math.min(Math.max(margin, top), maxTop),
    };
  };

  const dragRef = useRef({
    dragging: false,
    pointerId: null,
    sx: 0,
    sy: 0,
    sl: 0,
    st: 0,
  });

    const onDown = (e) => {
    if (e.button != null && e.button !== 0) return;

    // ✅ Don't start dragging when clicking controls in the titlebar
    if (e.target.closest?.("button, a, input, textarea, select")) return;

    e.preventDefault();
    dragRef.current.dragging = true;
    dragRef.current.pointerId = e.pointerId;
    dragRef.current.sx = e.clientX;
    dragRef.current.sy = e.clientY;
    dragRef.current.sl = pos.left;
    dragRef.current.st = pos.top;
    try {
        e.currentTarget.setPointerCapture(e.pointerId);
    } catch {}
    };

  const onMove = (e) => {
    if (!dragRef.current.dragging) return;
    if (dragRef.current.pointerId !== e.pointerId) return;
    const dx = e.clientX - dragRef.current.sx;
    const dy = e.clientY - dragRef.current.sy;
    setPos(clamp(dragRef.current.sl + dx, dragRef.current.st + dy));
  };

  const onUp = (e) => {
    if (dragRef.current.pointerId !== e.pointerId) return;
    dragRef.current.dragging = false;
    dragRef.current.pointerId = null;
  };

  return (
    <div className="w-win" style={{ left: pos.left, top: pos.top }}>
      <div
        className="w-titlebar"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
      >
        <div className="w-title">{title}</div>
        <div className="w-spacer" />
        <button className="w-close" onClick={onClose}>
          CLOSE
        </button>
      </div>

      <div className="w-body" style={bodyStyle}>
        {children}
      </div>
    </div>
  );
}
